import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileText, Check, Loader2, CreditCard } from "lucide-react";

export const TIERS = [
  {
    id: "mp3",
    name: "MP3 Lease",
    lookupKey: "beat_mp3",
    price: 29,
    format: "MP3 320kbps",
    tagline: "Demos, singles & content",
    rights: [
      "High-quality MP3 320kbps file, delivered instantly after checkout",
      "Commercial distribution up to agreed streaming limits",
      "Radio broadcast and music video use",
      "Non-exclusive — the beat stays in the store",
    ],
  },
  {
    id: "wav",
    name: "WAV Lease",
    lookupKey: "beat_wav",
    price: 59,
    format: "WAV 24-bit + MP3",
    tagline: "Serious releases & radio",
    rights: [
      "Studio-quality WAV 24-bit + MP3 320kbps files",
      "Commercial distribution up to agreed streaming limits",
      "Radio broadcast and music video use",
      "Non-exclusive — the beat stays in the store",
    ],
  },
  {
    id: "unlimited",
    name: "Unlimited",
    lookupKey: "beat_unlimited",
    price: 149,
    format: "WAV + Trackout Stems",
    tagline: "No caps, full stems",
    bestValue: true,
    rights: [
      "WAV 24-bit, MP3 and full trackout stems",
      "Unmetered audio streaming and commercial distribution",
      "Single project release, unlimited videos and live use",
      "Non-exclusive — the beat stays in the store",
    ],
  },
  {
    id: "exclusive",
    name: "Exclusive",
    lookupKey: "beat_exclusive",
    price: 499,
    format: "Everything + Ownership",
    tagline: "Yours. Off the shelf forever",
    rights: [
      "All files: WAV 24-bit, MP3 and full trackout stems",
      "Exclusive use — beat is retired from the public catalog",
      "Unlimited streams, sales, videos, radio and sync",
      "DGE retains baseline authorship & publishing recognition",
    ],
  },
];

const SECTIONS = [
  {
    title: "1. Ownership & Intellectual Property",
    body: "All instrumentals, beats, audio files, compositions, and master recordings remain the exclusive intellectual property and copyright of DGE: Cold-Blooded Records LLC™ and its producers (including Executive Producer Heather Da Boa). Purchase of a license grants a limited right to use the instrumental for recording a new vocal song, subject to the restrictions of your chosen tier (MP3 Lease, WAV Lease, Unlimited, or Exclusive Rights). The underlying composition copyright is never transferred unless an Exclusive Rights contract is executed in writing.",
  },
  {
    title: "2. License Tiers & Usage Rights",
    body: "MP3 / WAV Lease: grants rights for commercial distribution up to agreed streaming limits, radio broadcasts, and music video use — does not transfer ownership of the master or publishing rights. Unlimited Lease: grants unmetered audio streaming and commercial distribution rights for a single project release. Exclusive Rights: transfers exclusive use of the instrumental; once purchased exclusively, the beat is retired from the public catalog, though DGE retains baseline authorship and publishing recognition.",
  },
  {
    title: "3. Mandatory Credit & Attribution",
    body: "Licensee is strictly required to give clear, written credit to the producer and label on all digital distribution platforms (Spotify, Apple Music, YouTube, SoundCloud, TIDAL, etc.) and physical media packaging. Required credit format: \"(Prod. by Heather Da Boa)\" or \"Produced by DGE: Cold-Blooded Records LLC™\".",
  },
  {
    title: "4. Publishing & ASCAP® / PRO Registration",
    body: "DGE: Cold-Blooded Records LLC™ and its creators are official ASCAP® & PRO members. For all commercial releases utilizing a DGE instrumental, songwriting and publishing splits must be registered correctly with your Performing Rights Organization (PRO) as outlined in your specific license breakdown (typically a standard 50/50 or label-specified composer/producer split). Failure to register publishing splits correctly constitutes a breach of contract.",
  },
  {
    title: "5. Restrictions",
    body: "Licensee shall NOT resell, lease, rent, sub-license, or redistribute the raw instrumental audio file as a standalone beat or stock audio. Licensee shall NOT register the raw, unedited instrumental with Content ID systems (such as YouTube Content ID or DistroKid/TuneCore ID registries) as exclusive copyright ownership.",
  },
  {
    title: "6. Governing Law & Acceptance",
    body: "By checking out or downloading files from DGE: Cold-Blooded Records LLC™, you legally acknowledge that you have read, understood, and agreed to all terms outlined above. Copyright © 2026 DGE: Cold-Blooded Records LLC™. All Rights Reserved.",
  },
];

export const LicenseModal = ({ track, onClose, onBuy, onRequestQuote, buying }) => {
  useEffect(() => {
    if (!track) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [track, onClose]);
  const [tierId, setTierId] = useState("unlimited");

  const allowedIds = track?.format === "MP3" ? ["mp3"] : TIERS.map((t) => t.id);

  useEffect(() => {
    if (track) setTierId(track.format === "MP3" ? "mp3" : "wav");
  }, [track]);

  const tier = TIERS.find((t) => t.id === tierId && allowedIds.includes(t.id)) || TIERS[0];

  return (
    <AnimatePresence>
      {track && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-obsidian/85 backdrop-blur-md px-4 py-8"
          onClick={onClose}
          data-testid="license-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl max-h-[88vh] flex flex-col bg-charcoal border border-crystal/40 rounded-2xl glow-crystal overflow-hidden"
          >
            <div className="flex items-start justify-between gap-4 p-6 sm:p-8 pb-0">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-venom flex items-center gap-2">
                  <FileText size={13} /> Digital Beat Licensing Agreement
                </p>
                <h3 className="mt-2 font-display text-lg sm:text-xl font-bold text-white leading-snug">
                  &quot;{track.title}&quot;
                </h3>
                <p className="text-xs text-ice/50 mt-1">
                  {track.bpm ? `${track.bpm} BPM · ` : ""}{track.key ? `${track.key} · ` : ""}DGE: Cold-Blooded Records LLC™
                </p>
              </div>
              <button
                onClick={onClose}
                aria-label="Close licensing agreement"
                className="text-ice/50 hover:text-white transition-colors p-1.5"
                data-testid="license-modal-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-6 sm:px-8 mt-5">
              <div className="grid grid-cols-4 gap-1.5 bg-obsidian/70 border border-white/10 rounded-xl p-1.5" data-testid="license-tier-tabs">
                {TIERS.map((t) => {
                  const allowed = allowedIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      onClick={() => allowed && setTierId(t.id)}
                      disabled={!allowed}
                      title={allowed ? t.name : `Requires a ${t.format} master — this beat was uploaded as ${track.format}`}
                      className={`rounded-lg px-1 sm:px-2 py-2.5 text-center transition-all duration-300 ${
                        !allowed
                          ? "opacity-30 cursor-not-allowed"
                          : tierId === t.id
                            ? "bg-venom text-obsidian"
                            : "text-ice/60 hover:text-white hover:bg-white/5"
                      }`}
                      data-testid={`license-tab-${t.id}`}
                    >
                      <span className="block font-display text-[10px] sm:text-xs font-bold leading-tight">{t.name}</span>
                      <span className={`block font-mono text-[10px] mt-0.5 ${tierId === t.id && allowed ? "text-obsidian/80" : "text-venom"}`}>
                        ${t.price}
                      </span>
                    </button>
                  );
                })}
              </div>
              {track.format === "MP3" && (
                <p className="mt-2 text-[11px] text-ice/45 font-mono" data-testid="license-format-note">
                  This beat was uploaded as an MP3 master, so it's available as an MP3 Lease.
                </p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6">
              <div className="mb-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-venom mb-2">
                  {tier.name} · {tier.format} — What you get
                </p>
                <ul className="space-y-1.5">
                  {tier.rights.map((r) => (
                    <li key={r} className="flex gap-2 text-[13px] text-ice/75">
                      <Check size={13} className="text-venom shrink-0 mt-0.5" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-4 border-t border-white/10 pt-5">
                <p className="text-[11px] uppercase tracking-[0.15em] text-ice/45 font-mono">
                  Please read these terms carefully before purchasing or downloading any beat or instrumental
                </p>
                {SECTIONS.map((s) => (
                  <div key={s.title}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-venom mb-1.5">{s.title}</p>
                    <p className="text-[13px] leading-relaxed text-ice/65">{s.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 p-5 sm:p-6 bg-obsidian/50">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="font-display text-xl font-bold text-white">
                    ${tier.price}<span className="text-xs text-ice/50 font-body font-normal"> USD</span>
                  </p>
                  <p className="text-[11px] text-ice/45">{tier.name} · {tier.format}</p>
                </div>
                <button
                  onClick={() => onBuy(track, tier)}
                  disabled={buying}
                  className="inline-flex items-center justify-center gap-2 bg-venom text-obsidian font-bold text-sm px-7 py-3.5 rounded-full hover:glow-venom hover:scale-[1.03] active:scale-95 disabled:opacity-60 transition-all duration-300"
                  data-testid="license-buy-button"
                >
                  {buying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  {buying ? "Opening checkout…" : `Buy ${tier.name} — $${tier.price}`}
                </button>
              </div>
              <button
                onClick={() => onRequestQuote(track, tier)}
                className="mt-3 w-full text-center text-[11px] font-mono uppercase tracking-[0.15em] text-ice/40 hover:text-crystal transition-colors"
                data-testid="license-quote-link"
              >
                Need custom terms? Request this license via quote →
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
